import { useState } from 'react';
// Today's Focus — personalized priority list shown on each role dashboard.
//
// Pulls /me/today and renders five small sections:
//   alert     Overdue tasks (top 5)
//   clock     Due today
//   calendar  Interviews today
//   clock     Stalled candidates (>5 days no movement)
//   EOD report status (orange chip if not filed yet by 5pm local)
//
// Empty sections collapse so the widget doesn't take up screen unless
// there's actual work to surface. Clicking any row navigates to the
// underlying entity.
//
// The panel starts collapsed and remembers whether you opened it (see
// COLLAPSE_KEY below), so it never occupies half the dashboard on arrival.
// The header still shows the item count while collapsed, so nothing is
// hidden — you see "12 items" and expand only when you want the detail.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { meApi, tasksApi, type TodayFocus } from '../lib/api';
import { useToast } from './ToastHost';
import Icon, { IconName } from './ui/Icon';

function relTime(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const m = Math.round(ms / 60_000);
  if (m === 0) return 'now';
  if (m > 0 && m < 60) return `in ${m}m`;
  if (m < 0 && m > -60) return `${-m}m ago`;
  const h = Math.round(m / 60);
  if (h > 0 && h < 24) return `in ${h}h`;
  if (h < 0 && h > -24) return `${-h}h ago`;
  const d = Math.round(h / 24);
  if (d > 0) return `in ${d}d`;
  return `${-d}d ago`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Collapse state lives in localStorage like the other dashboard prefs
// (see lib/dashboardPrefs.ts) — personal taste, not audit-relevant, so
// there's no reason to round-trip it through the backend. Not keyed by
// user id because the widget takes no props and is dropped onto every
// dashboard; a shared-browser mismatch on a collapse toggle is harmless.
const COLLAPSE_KEY = 'fns_today_focus_collapsed_v1';

function readCollapsed(): boolean {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    // Default collapsed: the header still shows the item count, so the
    // work stays visible without the panel dominating the screen.
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}

function writeCollapsed(v: boolean): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0');
  } catch {
    // localStorage full or disabled — fail silently.
  }
}

export default function TodayFocusWidget() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['me-today'],
    queryFn: () => meApi.today().then(r => r.data),
    refetchInterval: 60_000,
  });

  // Collapsible state — declared with the other hooks, ABOVE any early return,
  // so hook order is identical on every render (React rules of hooks).
  // Seeded from localStorage so the panel reopens the way you left it.
  const [collapsed, setCollapsed] = useState(readCollapsed);

  function toggleCollapsed() {
    setCollapsed((v: boolean) => {
      const next = !v;
      writeCollapsed(next);
      return next;
    });
  }

  async function completeTask(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await tasksApi.complete(id);
      toast.success('Done');
      void refetch();
    } catch {
      toast.error('Failed to complete');
    }
  }

  if (isLoading || !data) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: 14, fontSize: 12, color: 'var(--text-muted)' }}>Loading today's focus…</div>
      </div>
    );
  }

  const totalItems =
    data.overdue_tasks.length + data.due_today_tasks.length +
    data.interviews_today.length + data.stalled_candidates.length;

  return (
    <div style={containerStyle}>
      <div style={{
        padding: '12px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid var(--border)', background: 'var(--surface-sunken)',
        borderRadius: '12px 12px 0 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'}>
          <Icon name="target" size={16} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Today's Focus</span>
          <span style={{ color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ display: 'inline-flex', transform: !collapsed ? undefined : 'rotate(-90deg)', transition: 'transform var(--dur) var(--ease-out)' }}><Icon name="chevron-down" size={11} /></span></span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {totalItems === 0 ? 'You\'re clear' : `${totalItems} items`}
          </span>
        </div>
        <EodChip filed={data.eod_filed} onClick={() => navigate('/end-of-day')} />
      </div>

      {collapsed ? null : totalItems === 0 ? (
        <div style={{ padding: '24px 16px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
          Nothing pressing right now. Run /huddle, file a /end-of-day at 5pm.
        </div>
      ) : (
        <div style={{ padding: '4px 0' }}>
          <Section
            title="Overdue"
            icon="alert"
            color="var(--danger-fg)"
            items={data.overdue_tasks}
            empty={false}
            render={(t: TodayFocus['overdue_tasks'][number]) => (
              <Row
                key={t.id}
                primary={t.title}
                secondary={[
                  t.candidate_name && t.candidate_name,
                  t.job_title && t.job_title,
                ].filter(Boolean).join(' · ')}
                meta={relTime(t.due_at)}
                metaColor="var(--danger-fg)"
                onClick={() => navigate('/tasks')}
                action={
                  <button
                    onClick={(e) => void completeTask(t.id, e)}
                    style={smallDoneBtn}
                    title="Mark done"
                  ><Icon name="check" size={13} /></button>
                }
              />
            )}
          />
          <Section
            title="Due today"
            icon="clock"
            color="var(--warn-fg)"
            items={data.due_today_tasks}
            empty={false}
            render={(t: TodayFocus['due_today_tasks'][number]) => (
              <Row
                key={t.id}
                primary={t.title}
                secondary={[
                  t.candidate_name && t.candidate_name,
                  t.job_title && t.job_title,
                ].filter(Boolean).join(' · ')}
                meta={fmtTime(t.due_at)}
                metaColor="var(--warn-fg)"
                onClick={() => navigate('/tasks')}
                action={
                  <button
                    onClick={(e) => void completeTask(t.id, e)}
                    style={smallDoneBtn}
                    title="Mark done"
                  ><Icon name="check" size={13} /></button>
                }
              />
            )}
          />
          <Section
            title="Interviews today"
            icon="calendar"
            color="var(--ai)"
            items={data.interviews_today}
            empty={false}
            render={(i: TodayFocus['interviews_today'][number]) => (
              <Row
                key={i.id}
                primary={`${i.candidate_name} · ${i.job_title}`}
                secondary={i.client_name ?? undefined}
                meta={fmtTime(i.interview_scheduled_at)}
                metaColor="var(--ai)"
                onClick={() => navigate(`/submissions/${i.id}`)}
              />
            )}
          />
          <Section
            title="Stalled candidates"
            icon="clock"
            color="var(--text)"
            items={data.stalled_candidates}
            empty={false}
            render={(c: TodayFocus['stalled_candidates'][number]) => (
              <Row
                key={c.id}
                primary={c.candidate_name}
                secondary={c.stage ? `in ${c.stage}` : undefined}
                meta={`${c.days_stalled}d no movement`}
                metaColor="var(--text)"
                onClick={() => navigate(`/candidates/${c.id}`)}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  marginBottom: 16,
  overflow: 'hidden',
};

const smallDoneBtn: React.CSSProperties = {
  padding: '2px 8px', fontSize: 11, fontWeight: 700,
  background: 'var(--good-tint)', color: 'var(--good-fg)',
  border: '1px solid var(--good-border)', borderRadius: 6,
  cursor: 'pointer',
};

function Section<T>({
  title, icon, color, items, empty, render,
}: {
  title: string; icon: IconName; color: string;
  items: T[]; empty: boolean;
  render: (item: T) => React.ReactNode;
}) {
  if (items.length === 0 && !empty) return null;
  return (
    <div style={{ borderBottom: '1px solid var(--surface-subtle)' }}>
      <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        <span style={{ marginRight: 4, display: 'inline-flex', verticalAlign: 'middle' }}>
          <Icon name={icon} size={12} />
        </span>{title} ({items.length})
      </div>
      <div>
        {items.map(item => render(item))}
      </div>
    </div>
  );
}

function Row({
  primary, secondary, meta, metaColor, onClick, action,
}: {
  primary: string;
  secondary?: string;
  meta?: string;
  metaColor?: string;
  onClick?: () => void;
  action?: React.ReactNode;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        cursor: onClick ? 'pointer' : 'default',
        fontSize: 13,
        borderTop: '1px solid var(--surface-sunken)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#fafbfc'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: 'var(--text-strong)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {primary}
        </div>
        {secondary && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {secondary}
          </div>
        )}
      </div>
      {meta && (
        <span style={{ fontSize: 11, fontWeight: 600, color: metaColor ?? 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {meta}
        </span>
      )}
      {action}
    </div>
  );
}

function EodChip({ filed, onClick }: { filed: boolean; onClick: () => void }) {
  const isLate = !filed && new Date().getHours() >= 17;
  if (filed) {
    return (
      <span style={{ padding: '3px 9px', borderRadius: 999, background: 'var(--good-tint)', color: 'var(--good-fg)', fontSize: 11, fontWeight: 700 }}>
        EOD filed
      </span>
    );
  }
  return (
    <button
      onClick={onClick}
      style={{
        padding: '3px 9px', borderRadius: 999,
        background: isLate ? 'var(--warn-tint)' : 'var(--surface-subtle)',
        color: isLate ? 'var(--warn-fg)' : 'var(--text)',
        border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
      }}
      title={isLate ? 'You haven\'t filed your end-of-day report yet — it\'s past 5pm' : 'File your end-of-day report'}
    >
      {isLate ? 'EOD pending' : 'File EOD'}
    </button>
  );
}
